import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ArrowLeftIcon,
  CubeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CpuChipIcon,
  CircleStackIcon,
  CodeBracketIcon,
  RocketLaunchIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import { Project, Environment, Deployment, DeploymentDetail, DeploymentUpdate } from '../types/api';
import QuotaErrorModal from '../components/QuotaErrorModal';
import Logger from '../utils/logger';
import { getApiErrorMessage } from '../constants/messages';
import { useTranslation } from 'react-i18next';

const ProjectApplicationsPage: React.FC = () => {
  const { t } = (useTranslation as any)('projects');
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [allDeployments, setAllDeployments] = useState<{ [envId: string]: Deployment[] }>({});
  const [deploymentDetails, setDeploymentDetails] = useState<{ [deploymentId: string]: DeploymentDetail }>({});
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDeployment, setEditingDeployment] = useState<Deployment | null>(null);
  const [editForm, setEditForm] = useState<DeploymentUpdate>({});
  const [quotaError, setQuotaError] = useState<any>(null);
  const [showQuotaError, setShowQuotaError] = useState(false);

  useEffect(() => {
    if (projectId) {
      loadProjectApplications();
    }
  }, [projectId]);

  const loadProjectApplications = async () => {
    if (!projectId) return;

    try {
      setLoading(true);

      // Charger projet et environnements en parallèle
      const [projectData, environmentsData] = await Promise.all([
        apiService.getProject(projectId),
        apiService.getProjectEnvironments(projectId)
      ]);

      setProject(projectData);
      setEnvironments(environmentsData);

      // Charger les déploiements pour chaque environnement
      const deploymentPromises = environmentsData.map(async (env) => {
        try {
          const deployments = await apiService.getEnvironmentDeployments(env.id);
          return { envId: env.id, deployments };
        } catch (error) {
          Logger.warn('Failed to load deployments for environment');
          return { envId: env.id, deployments: [] };
        }
      });

      const deploymentsResults = await Promise.all(deploymentPromises);
      const deploymentsMap: { [envId: string]: Deployment[] } = {};

      deploymentsResults.forEach(({ envId, deployments }) => {
        deploymentsMap[envId] = deployments;
      });

      setAllDeployments(deploymentsMap);

      // Charger les détails de chaque déploiement
      const allDeploymentsList = deploymentsResults.flatMap(({ deployments }) => deployments);
      // Loading deployment details

      if (allDeploymentsList.length > 0) {
        const detailsPromises = allDeploymentsList.map(async (deployment) => {
          try {
            // Loading deployment details
            const details = await apiService.getDeploymentDetails(deployment.id);
            // Deployment details loaded successfully
            return { deploymentId: deployment.id, details };
          } catch (error) {
            Logger.error('Failed to load deployment details');
            return { deploymentId: deployment.id, details: null };
          }
        });

        const detailsResults = await Promise.all(detailsPromises);
        const detailsMap: { [deploymentId: string]: DeploymentDetail } = {};

        detailsResults.forEach(({ deploymentId, details }) => {
          if (details) {
            // Storing deployment details in cache
            detailsMap[deploymentId] = details;
          }
        });

        // Deployment details cache populated
        setDeploymentDetails(detailsMap);
      }
    } catch (error: unknown) {
      Logger.error('Failed to load project applications data');
      toast.error(t('applications.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'deploye':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'erreur':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'en_cours':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-text-quaternary" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'deploye':
        return t('applications.status.deployed');
      case 'erreur':
        return t('applications.status.error');
      case 'en_cours':
        return t('applications.status.inProgress');
      default:
        return t('applications.status.unknown');
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'python':
      case 'node':
      case 'web':
        return <CodeBracketIcon className="h-5 w-5 text-accent-500" />;
      case 'odoo':
      case 'wordpress':
        return <RocketLaunchIcon className="h-5 w-5 text-purple-500" />;
      default:
        return <CubeIcon className="h-5 w-5 text-text-tertiary" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleEditDeployment = (deployment: Deployment) => {
    setEditingDeployment(deployment);
    setEditForm({
      nom: deployment.nom,
      cpu_requis: deployment.cpu_requis,
      ram_requise: deployment.ram_requise,
      replicas: deployment.replicas,
      image_registry_url: deployment.image_registry_url || '',
      image_tag: deployment.image_tag || '',
      nom_de_domaine: deployment.nom_de_domaine || '',
      helm_chart_version: deployment.helm_chart_version
    });
    setShowEditModal(true);
  };

  const handleUpdateDeployment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDeployment) return;

    try {
      await apiService.updateDeployment(editingDeployment.id, editForm);
      toast.success(t('applications.updateSuccess'));
      setShowEditModal(false);
      setEditingDeployment(null);
      setEditForm({});
      // Recharger les données
      loadProjectApplications();
    } catch (error: unknown) {
      Logger.error('Failed to update deployment', error);

      // Check if it's a quota error
      const apiError = error as { response?: { status?: number; data?: { detail?: { message?: string; quota_exceeded?: boolean } } } };
      if (apiError.response?.status === 400 && apiError.response?.data?.detail &&
          typeof apiError.response.data.detail === 'object' &&
          apiError.response.data.detail.message &&
          apiError.response.data.detail.quota_exceeded) {
        // It's a quota error, show the quota modal
        setQuotaError(apiError.response.data.detail);
        setShowQuotaError(true);
      } else {
        toast.error(getApiErrorMessage(error) || t('applications.updateError'));
      }
    }
  };

  // Compter le total des applications
  const totalApplications = Object.values(allDeployments).reduce((sum, deployments) => sum + deployments.length, 0);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500 mx-auto"></div>
        <p className="mt-4 text-text-tertiary">{t('applications.loadingApps')}</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">{t('applications.notFound')}</p>
        <Link to="/projects" className="text-accent-400 hover:text-accent-300">
          {`← ${t('applications.backToProjects')}`}
        </Link>
      </div>
    );
  }

  return (
    <>
      {/* Breadcrumb */}
      <nav className="flex mb-8" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-4">
          <li>
            <Link to="/projects" className="text-text-quaternary hover:text-text-tertiary">
              <ArrowLeftIcon className="flex-shrink-0 h-5 w-5" />
              <span className="ml-2">{t('applications.breadcrumbProjects')}</span>
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <span className="text-text-quaternary">/</span>
              <Link to={`/projects/${projectId}`} className="ml-4 text-text-quaternary hover:text-text-tertiary">
                {project.nom}
              </Link>
            </div>
          </li>
          <li>
            <div className="flex items-center">
              <span className="text-text-quaternary">/</span>
              <span className="ml-4 text-text-tertiary">{t('applications.breadcrumbApplications')}</span>
            </div>
          </li>
        </ol>
      </nav>

      {/* Header */}
      <div className="lg:flex lg:items-center lg:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-text-primary sm:text-3xl sm:truncate">
            {t('applications.title')}
          </h2>
          <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
            <div className="mt-2 flex items-center text-sm text-text-tertiary">
              <CubeIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-text-quaternary" />
              {t('applications.appCount', { count: totalApplications })}
            </div>
            <div className="mt-2 flex items-center text-sm text-text-tertiary">
              {t('applications.project')}: <strong className="ml-1">{project.nom}</strong>
            </div>
          </div>
        </div>
      </div>

      {/* Applications par environnement */}
      <div className="mt-8">
        {environments.length === 0 ? (
          <div className="text-center py-12">
            <CubeIcon className="mx-auto h-12 w-12 text-text-quaternary" />
            <h3 className="mt-2 text-sm font-medium text-text-primary">{t('applications.noEnvironments')}</h3>
            <p className="mt-1 text-sm text-text-tertiary">
              {t('applications.createEnvironmentsFirst')}
            </p>
            <div className="mt-6">
              <Link
                to={`/environments/${projectId}`}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
              >
                {t('applications.manageEnvironments')}
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {environments.map((environment) => {
              const deployments = allDeployments[environment.id] || [];

              return (
                <div key={environment.id} className="bg-surface-2 shadow-card rounded-card">
                  <div className="px-4 py-5 sm:px-6 border-b border-border-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg leading-6 font-medium text-text-primary">
                          {t('applications.environment')}: {environment.nom}
                        </h3>
                        <p className="mt-1 text-sm text-text-tertiary">
                          {t('applications.deployedApps', { count: deployments.length })}
                        </p>
                      </div>
                      <Link
                        to={`/deployments/${environment.id}`}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-blue-400 bg-blue-500/12 hover:bg-blue-500/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        {t('applications.manage')} →
                      </Link>
                    </div>
                  </div>

                  <div className="px-4 py-5 sm:p-6">
                    {deployments.length === 0 ? (
                      <div className="text-center py-4">
                        <CubeIcon className="mx-auto h-8 w-8 text-text-quaternary" />
                        <p className="mt-2 text-sm text-text-tertiary">
                          {t('applications.noDeployedApps')}
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {deployments.map((deployment) => {
                          const details = deploymentDetails[deployment.id];
                          // Rendering deployment component
                          return (
                            <div
                              key={deployment.id}
                              className="border border-border-1 rounded-lg p-4 bg-surface-2 hover:shadow-md transition-shadow"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <div className="flex-shrink-0">
                                    {getTypeIcon(deployment.type_application)}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-medium text-text-primary truncate">
                                      {deployment.nom}
                                    </h4>
                                    <p className="text-xs text-text-tertiary truncate">
                                      {deployment.type_application}
                                    </p>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleEditDeployment(deployment)}
                                  className="p-1 text-text-quaternary hover:text-blue-400 hover:bg-blue-500/10 rounded"
                                  title={t('applications.editDeployment')}
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                              </div>

                              {/* Status */}
                              <div className="mt-3 flex items-center">
                                {getStatusIcon(details?.status || deployment.statut_deploiement)}
                                <span className="ml-2 text-sm font-medium text-text-primary">
                                  {getStatusText(details?.status || deployment.statut_deploiement)}
                                </span>
                              </div>

                              {/* Ressources */}
                              <div className="mt-3 grid grid-cols-2 gap-2">
                                <div className="flex items-center">
                                  <CpuChipIcon className="h-4 w-4 mr-1 text-text-quaternary" />
                                  <span className="text-xs font-medium text-text-tertiary">CPU:</span>
                                  <span className="ml-1 text-xs font-medium text-text-primary">{deployment.cpu_requis}</span>
                                </div>
                                <div className="flex items-center">
                                  <CircleStackIcon className="h-4 w-4 mr-1 text-text-quaternary" />
                                  <span className="text-xs font-medium text-text-tertiary">RAM:</span>
                                  <span className="ml-1 text-xs font-medium text-text-primary">{deployment.ram_requise} GB</span>
                                </div>
                              </div>

                              {/* Détails supplémentaires */}
                              <div className="mt-3 space-y-1 text-xs">
                                <div>
                                  <span className="font-medium text-text-tertiary">{t('applications.fields.status')}:</span>
                                  <span className="ml-1 text-text-primary">{details?.status || deployment.statut_deploiement || t('applications.status.unknown')}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-text-tertiary">{t('applications.fields.repoUrl')}:</span>
                                  <span className="ml-1 text-text-primary">{details?.repository_url || deployment.repo_url || 'N/A'}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-text-tertiary">{t('applications.fields.imageRegistry')}:</span>
                                  <span className="ml-1 text-text-primary">{details?.image_url || deployment.image_registry_url || 'N/A'}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-text-tertiary">{t('applications.fields.cpuRequired')}:</span>
                                  <span className="ml-1 text-text-primary">{details?.cpu_required || deployment.cpu_requis || 'N/A'} cores</span>
                                </div>
                                <div>
                                  <span className="font-medium text-text-tertiary">{t('applications.fields.ramRequired')}:</span>
                                  <span className="ml-1 text-text-primary">{details?.ram_required || deployment.ram_requise || 'N/A'} GB</span>
                                </div>
                                <div>
                                  <span className="font-medium text-text-tertiary">{t('applications.fields.updatedAt')}:</span>
                                  <span className="ml-1 text-text-primary">{
                                    details?.updated_at ? formatDate(details.updated_at) :
                                    (deployment.derniere_mise_a_jour ? formatDate(deployment.derniere_mise_a_jour) : 'N/A')
                                  }</span>
                                </div>
                              </div>

                              {/* URL si disponible et app exposée publiquement */}
                              {details?.expose_public !== false && (details?.domain || deployment.nom_de_domaine) && (
                                <div className="mt-2">
                                  <a
                                    href={`https://${details?.domain || deployment.nom_de_domaine}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-accent-400 hover:text-accent-300 truncate block"
                                  >
                                    {details?.domain || deployment.nom_de_domaine}
                                  </a>
                                </div>
                              )}

                              {/* Date */}
                              <div className="mt-3 text-xs">
                                <span className="font-medium text-text-tertiary">{t('applications.fields.deployedAt')}:</span>
                                <span className="ml-1 text-text-primary">{formatDate(details?.created_at || deployment.date_creation)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal d'édition */}
      {showEditModal && editingDeployment && (
        <div className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-border-1 w-96 shadow-lg rounded-md bg-surface-2">
            <h3 className="text-lg font-bold text-text-primary mb-4">
              {t('applications.editDeploymentTitle', { name: editingDeployment.nom })}
            </h3>
            <form onSubmit={handleUpdateDeployment}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary">{t('name', { ns: 'common' })}</label>
                  <input
                    type="text"
                    value={editForm.nom || ''}
                    onChange={(e) => setEditForm({ ...editForm, nom: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary">CPU</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={editForm.cpu_requis || 0}
                      onChange={(e) => setEditForm({ ...editForm, cpu_requis: parseFloat(e.target.value) })}
                      className="mt-1 block w-full px-3 py-2 bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary">RAM (GB)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={editForm.ram_requise || 0}
                      onChange={(e) => setEditForm({ ...editForm, ram_requise: parseFloat(e.target.value) })}
                      className="mt-1 block w-full px-3 py-2 bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary">{t('applications.fields.replicas')}</label>
                  <input
                    type="number"
                    min="1"
                    value={editForm.replicas || 1}
                    onChange={(e) => setEditForm({ ...editForm, replicas: parseInt(e.target.value) })}
                    className="mt-1 block w-full px-3 py-2 bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary">{t('applications.fields.imageRegistryUrl')}</label>
                  <input
                    type="text"
                    value={editForm.image_registry_url || ''}
                    onChange={(e) => setEditForm({ ...editForm, image_registry_url: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary">{t('applications.fields.imageTag')}</label>
                  <input
                    type="text"
                    value={editForm.image_tag || ''}
                    onChange={(e) => setEditForm({ ...editForm, image_tag: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary">{t('applications.fields.domainName')}</label>
                  <input
                    type="text"
                    value={editForm.nom_de_domaine || ''}
                    onChange={(e) => setEditForm({ ...editForm, nom_de_domaine: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                    placeholder="exemple.com"
                  />
                </div>

              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingDeployment(null);
                    setEditForm({});
                  }}
                  className="px-4 py-2 text-sm font-medium text-text-secondary bg-surface-3 rounded-md hover:bg-surface-4"
                >
                  {t('cancel', { ns: 'common' })}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-accent-600 rounded-md hover:bg-accent-700"
                >
                  {t('save', { ns: 'common' })}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quota Error Modal */}
      <QuotaErrorModal
        isOpen={showQuotaError}
        onClose={() => {
          setShowQuotaError(false);
          setQuotaError(null);
        }}
        quotaError={quotaError || {}}
      />
    </>
  );
};

export default ProjectApplicationsPage;
